import type { ComponentType, Dispatch, Key, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import {
  BookPlus,
  Columns3,
  Contrast,
  Copy,
  Download,
  Eye,
  FileArchive,
  FolderDownIcon,
  FolderOpen,
  FolderPlus,
  FolderUpIcon,
  Heart,
  Inbox,
  ListX,
  LogOut,
  type LucideIcon,
  Moon,
  MousePointerClick,
  Pen,
  RotateCcwIcon,
  Rows3,
  Share2,
  Sun,
  Trash2,
  Upload,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { type Row } from '@tanstack/react-table';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CommentAllFilter,
  CommentResolutionFilter,
  type CommentResolutionFilterOption,
  CommentVisibilityFilter,
  type CommentVisibilityFilterOption,
  type NodeComment,
} from '@/types/comments';
import {
  SortOrderOptions,
  SortOrderValues,
  albumSortOptions,
  baseSortOptions,
  baseSortOrderOptions,
  shareSortOptions,
  sortOptionsByEntity,
  trashSortOptions,
} from '@/types/sort';
import {
  selectIsZoomInDisabled,
  selectIsZoomOutDisabled,
  useUserSettingsStore,
} from '@/hooks/data/settings/useUserSettingsStore';
import { DAMNodeType, type NodeType, type TreeRecursiveChildrenNode, type TreeRootNode } from '@/types/tree';
import { useDeleteNodeCommentMutation } from '@/hooks/comments/useDeleteNodeCommentMutation';
import { MenuCheckboxItem, MenuItem, useMenuPrimitives } from '@/components/menu-renderer';
import { useActivateAssetVersion } from '@/hooks/data/assets/useActivateAssetVersion';
import { useDuplicateAssetAction } from '@/hooks/data/assets/useDuplicateAssetAction';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { deleteAssetVersion, getAsset } from '@/services/asset.service';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { usePerformLogout } from '@/hooks/data/account/useAccount';
import { shareHasDownloadableContent } from '@/utilities/share';
import { useNodeFavorite } from '@/hooks/data/useNodeFavorite';
import { useDataTablePagination } from '@/hooks/usePagination';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTrash } from '@/hooks/data/trash/useTrash';
import { useToast } from '@/components/ui/use-toast';
import { useTree } from '@/hooks/data/tree/useTree';
import { useDownload } from '@/hooks/useDownload';
import { ColumnType } from '@/types/column';
import { EntityType } from '@/types/entity';
import { QueryKey } from '@/lib/queryKeys';
import { type Asset } from '@/types/asset';
import { cn, isAsset } from '@/lib/utils';
import { GalleryView } from '@/types';
import { log } from '@/utilities/log';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';

type NodeKind = DAMNodeType.Album | DAMNodeType.Folder;

const modalToCreateNode: Record<NodeKind, ModalId> = {
  album: ModalId.CreateAlbum,
  folder: ModalId.CreateFolder,
};

const modalToRenameNode: Record<NodeKind, ModalId> = {
  album: ModalId.EditAlbum,
  folder: ModalId.EditFolder,
};

type BaseMenuVisibilityProps = {
  disabled?: boolean;
};

export const NewNodeMenuItem = ({
  createType,
  parentId,
  disabled,
  icon: Icon = FolderPlus,
  dataCy,
}: BaseProps & { createType: DAMNodeType }) => {
  const openModal = useOpenModal();

  const handleSelect = useCallback(() => {
    openModal(modalToCreateNode[createType as NodeKind], parentId ? { id: parentId } : undefined);
  }, [openModal, parentId, createType]);

  const defaultLabel = createType === DAMNodeType.Album ? 'New Album' : 'New Folder';

  return (
    <MenuItem icon={Icon} onSelect={handleSelect} disabled={disabled} dataCy={dataCy ?? `new-${createType}-action`}>
      {defaultLabel}
    </MenuItem>
  );
};

type BaseProps = {
  parentId?: string;
  node?: Partial<TreeRootNode>;
  disabled?: boolean;
  label?: string;
  icon?: ComponentType<{ className?: string }>;
  dataCy?: string;
};

export const NewFolderMenuItem = (
  props: Omit<BaseProps, 'label' | 'icon'> & {
    label?: string;
    icon?: ComponentType<{ className?: string }>;
  },
) => <NewNodeMenuItem createType={DAMNodeType.Folder} label={props.label} icon={props.icon ?? FolderPlus} {...props} />;

export const NewAlbumMenuItem = (
  props: Omit<BaseProps, 'label' | 'icon'> & {
    label?: string;
    icon?: ComponentType<{ className?: string }>;
  },
) => <NewNodeMenuItem createType={DAMNodeType.Album} label={props.label} icon={props.icon ?? BookPlus} {...props} />;

type BasicNodeInfo = {
  id: string;
  name: string;
};

type DeleteNodeMenuItemNode = Omit<Partial<TreeRootNode>, 'id' | 'node' | 'type'> & {
  id: string;
  // Both vocabularies, deliberately: data-table-draggable-item passes NodeType.Assets ('assets')
  // when it uses this item purely to remove assets from an album, and `isAsset` accepts that
  // spelling alongside DAMNodeType.Asset. Was `TreeRootNode['type']`, which only permitted it
  // because that field used to be an unvalidated `z.string()`.
  type?: DAMNodeType | NodeType;
  node?: (Partial<TreeRootNode['node']> & { name: string }) | undefined;
};

export const NavigateToNodeMenuItem = ({
  node,
  onSelect,
  disabled,
  className,
  labelClassName,
  dataCy,
}: {
  node: BasicNodeInfo;
  onSelect: (node: BasicNodeInfo) => void;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  dataCy?: string;
}) => (
  <MenuItem
    onSelect={() => onSelect(node)}
    disabled={disabled}
    dataCy={dataCy}
    className={cn(className, { 'hover:bg-[unset]!': disabled })}
    labelClassName={cn('block max-w-[200px] truncate', labelClassName)}
  >
    {node.name}
  </MenuItem>
);

export const RenameNodeMenuItem = ({ node: { id, type, node } }: { node: Partial<TreeRootNode> }) => {
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');

  return (
    <MenuItem
      icon={Pen}
      onSelect={() => openModal(modalToRenameNode[type as NodeKind], { id: id })}
      disabled={node?.locked}
      dataCy="rename-action"
    >
      {tCommon('rename')}
    </MenuItem>
  );
};

export const DeleteNodeMenuItem = ({
  node,
  disabled,
  danger,
  dataCy,
  isUpload,
  confirmationCallback,
  // True when this instance always targets exactly the passed-in `node`, ignoring
  // whatever else happens to be selected in the gallery — e.g. the upload task list's
  // per-file menu, where `node` is that one row and gallery selection is unrelated state.
  // Without this, the branch below falls back to inferring "single node" from
  // `confirmationCallback` being truthy, which silently breaks the moment that callback
  // becomes a no-op (or is omitted) for an otherwise-still-single-target caller.
  targetsSingleNode,
  assetCount,
  label = 'Trash',
  icon = Trash2,
}: {
  node?: DeleteNodeMenuItemNode;
  disabled?: boolean;
  danger?: boolean;
  label?: string;
  dataCy?: string;
  isUpload?: boolean;
  confirmationCallback?: () => void;
  targetsSingleNode?: boolean;
  assetCount?: number;
  icon?: LucideIcon;
}) => {
  const openModal = useOpenModal();
  const nodeId = node?.id;
  const nodeType = node?.type;
  const nodeName = node?.node?.name ?? '';

  const { selectedAssets } = useGallerySelection();
  const { removeAssets, getActiveUploadSession } = useUploadStore();
  const { uploadSessionId } = getActiveUploadSession();

  const handleSelect = useCallback(() => {
    if (isUpload) {
      removeAssets(uploadSessionId);
      return;
    }

    const isAssetType = isAsset(nodeType);
    const hasMultipleAssetSelection = !targetsSingleNode && isAssetType && selectedAssets.length !== 1;

    if (!nodeId && !hasMultipleAssetSelection && !targetsSingleNode) {
      return;
    }

    openModal(
      ModalId.DeleteConfirmation,
      hasMultipleAssetSelection
        ? {
            count: assetCount ?? selectedAssets.length,
            data: {
              type: nodeType ?? DAMNodeType.Asset,
            },
          }
        : {
            id: nodeId,
            name: nodeName,
            data: {
              type: nodeType,
              ...(confirmationCallback && { callBack: confirmationCallback }),
            },
            ...(assetCount !== undefined ? { count: assetCount } : {}),
          },
    );
  }, [
    openModal,
    nodeId,
    nodeName,
    nodeType,
    isUpload,
    removeAssets,
    uploadSessionId,
    selectedAssets.length,
    confirmationCallback,
    targetsSingleNode,
    assetCount,
  ]);

  return (
    <MenuItem
      icon={icon}
      danger={danger}
      onSelect={handleSelect}
      disabled={disabled}
      dataCy={dataCy ?? 'delete-action'}
    >
      {label}
    </MenuItem>
  );
};

export const FavoriteNodeMenuItem = ({
  node: { id, favorite, type },
  disabled,
}: {
  node: Partial<TreeRootNode>;
  disabled?: boolean;
}) => {
  const { toggleFavorite } = useNodeFavorite();
  const tMenu = useTranslations('menu.favorites');
  const label = favorite ? tMenu('remove') : tMenu('add');

  return (
    <MenuItem
      icon={Heart}
      iconClassName={cn({ 'fill-current': favorite })}
      onSelect={() => toggleFavorite(id as string, Boolean(favorite), type as DAMNodeType)}
      disabled={disabled}
      dataCy="favorite-action"
    >
      {label}
    </MenuItem>
  );
};

export const ShareAlbumMenuItem = ({
  node: { id, name },
  disabled,
}: {
  node: { id: string; name?: string };
  disabled?: boolean;
}) => {
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');

  const handleSelect = useCallback(() => {
    openModal(ModalId.EditCreateShare, { mode: 'create', nodeId: id, name, shareType: DAMNodeType.Album });
  }, [id, name, openModal]);

  return (
    <MenuItem icon={Share2} onSelect={handleSelect} disabled={disabled} dataCy="share-action">
      {tCommon('share')}
    </MenuItem>
  );
};

export const CreateSubmissionLinkMenuItem = ({
  node: { id, name },
  disabled,
}: {
  node: { id: string; name?: string };
  disabled?: boolean;
}) => {
  const isEnabled = useFeatureStore((s) => s.asset_submission);
  const { canUpload, canManageSubmissionLinks } = useCapabilities();
  const openModal = useOpenModal();
  const tSubmissions = useTranslations('submissions');

  const handleSelect = useCallback(() => {
    openModal(ModalId.EditCreateSubmissionLink, { mode: 'create', nodeId: id, folderName: name ?? '' });
  }, [id, name, openModal]);

  if (!isEnabled || !canUpload || !canManageSubmissionLinks) {
    return null;
  }

  return (
    <MenuItem icon={Inbox} onSelect={handleSelect} disabled={disabled} dataCy="create-submission-link-action">
      {tSubmissions('menuItem')}
    </MenuItem>
  );
};

export const ShareAssetMenuItem = ({ asset, disabled }: { asset: Asset; disabled?: boolean }) => {
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');

  const handleSelect = useCallback(() => {
    openModal(ModalId.EditCreateShare, {
      mode: 'create',
      nodeId: asset.nodeId,
      name: asset.name,
      shareType: DAMNodeType.Asset,
      description: asset.description,
      asset,
    });
  }, [asset, openModal]);

  return (
    <MenuItem icon={Share2} onSelect={handleSelect} disabled={disabled} dataCy="share-action">
      {tCommon('share')}
    </MenuItem>
  );
};

type UploadToFolderMenuItemProps = BaseMenuVisibilityProps & {
  folderId: string;
  /**
   * The folder's own full name path, when the caller already knows it (the browser view's
   * children section — DM-2518 follow-up). `findNodePath` only sees nodes the sidebar tree has
   * expanded, which a folder rendered solely in that section never is, so falling back to it there
   * silently no-ops instead of opening the upload dialog.
   */
  namePath?: Array<string>;
};

export const UploadToFolderMenuItem = ({ folderId, namePath, disabled }: UploadToFolderMenuItemProps) => {
  const tUpload = useTranslations('upload');
  const { setUploadModalOpen, setUploadFolderPath, getActiveUploadSession } = useUploadStore();
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const findNodePath = useTreeStore(useShallow((state) => state.findNodePath));

  const handleSelect = useCallback(() => {
    const pathArray = namePath ?? (findNodePath(folderId, 'name') as Array<string> | null);
    if (!pathArray) {
      return;
    }
    const rawPath = pathArray.join('/');
    const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

    const { uploadSessionId } = getActiveUploadSession();
    if (uploadSessionId) {
      setUploadFolderPath(uploadSessionId, normalizedPath);
      // A plain folder upload has no album target — without this, an album picked in a previous
      // upload attempt on the same session (abandoned by dismissing the "are you sure?" prompt
      // itself rather than confirming it, which skips the album-clearing cleanup below) stays
      // selected here too.
      useUploadStore.getState().setSelectedAlbums(uploadSessionId, []);
    }
    setReusableTreeSelectedFolder(folderId);
    setUploadModalOpen(true);
  }, [
    folderId,
    namePath,
    findNodePath,
    getActiveUploadSession,
    setReusableTreeSelectedFolder,
    setUploadFolderPath,
    setUploadModalOpen,
  ]);

  return (
    <MenuItem icon={Upload} onSelect={handleSelect} disabled={disabled} dataCy="upload-to-folder-action">
      {tUpload('upload')}
    </MenuItem>
  );
};

type UploadToAlbumMenuItemProps = BaseMenuVisibilityProps & {
  albumId: string;
  albumName: string;
  /**
   * The album's parent folder, when the caller already knows it (the browser view's children
   * section — DM-2518 follow-up). `findNodePath` only sees nodes the sidebar tree has expanded,
   * which an album rendered solely in that section never is, so falling back to it there silently
   * resets the upload dialog's folder preselection to the root instead of the album's real parent.
   */
  parent?: { id: string; namePath: Array<string> };
};

export const UploadToAlbumMenuItem = ({ albumId, albumName, parent, disabled }: UploadToAlbumMenuItemProps) => {
  const tUpload = useTranslations('upload');
  const { setUploadModalOpen, setUploadFolderPath, getActiveUploadSession } = useUploadStore();
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const setReusableTreeSelection = useTreeStore((state) => state.setReusableTreeSelection);

  const findNodePath = useTreeStore(useShallow((state) => state.findNodePath));

  const handleSelect = useCallback(() => {
    const { uploadSessionId } = getActiveUploadSession();

    let parentNodePath: string;
    let parentId: string | undefined;
    if (parent) {
      parentNodePath = parent.namePath.length > 0 ? `/${parent.namePath.join('/')}` : '';
      parentId = parent.id;
    } else {
      const namePath = (findNodePath(albumId, 'name') ?? []) as Array<string>;
      const idPath = (findNodePath(albumId, 'id') ?? []) as Array<string>;
      parentNodePath = namePath.length > 1 ? `/${namePath.slice(0, -1).join('/')}` : '';
      parentId = idPath.length > 1 ? idPath[idPath.length - 2] : undefined;
    }

    // Preselect parent folder
    setUploadFolderPath(uploadSessionId, parentNodePath);
    if (parentId) {
      setReusableTreeSelectedFolder(parentId);
    } else {
      setReusableTreeSelectedFolder(undefined);
      setReusableTreeSelection(undefined);
    }

    // Preselect selected album in the upload form
    useUploadStore.getState().setSelectedAlbums(uploadSessionId, [{ id: albumId, label: albumName }]);

    setUploadModalOpen(true);
  }, [
    albumId,
    albumName,
    parent,
    findNodePath,
    getActiveUploadSession,
    setReusableTreeSelectedFolder,
    setReusableTreeSelection,
    setUploadFolderPath,
    setUploadModalOpen,
  ]);

  return (
    <MenuItem icon={Upload} onSelect={handleSelect} disabled={disabled} dataCy="upload-to-album-action">
      {tUpload('upload')}
    </MenuItem>
  );
};

type ExpandChildrenFoldersMenuItemProps = BaseMenuVisibilityProps & {
  folderId: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
};

export const ExpandChildrenFoldersMenuItem = ({
  folderId,
  setLoading,
  setOpenIds,
  disabled,
}: ExpandChildrenFoldersMenuItemProps) => {
  const { getNodeWithChildren } = useTree();

  const queryClient = useQueryClient();

  // Recursive function to collect all folder IDs that have children and inject them into cache.
  const collectFolderIdsWithChildren = (
    node: TreeRecursiveChildrenNode,
    folderIds: Array<string> = [],
  ): Array<string> => {
    if (node.type === DAMNodeType.Folder && node.children && node.children.length > 0) {
      folderIds.push(node.id);
      queryClient.setQueryData([QueryKey.Tree, { nodeId: node.id }], node);
      node.children.forEach((child) => {
        collectFolderIdsWithChildren(child, folderIds);
      });
    }

    return folderIds;
  };

  const handleSelect = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch the node with children using the query client directly
      const result = await getNodeWithChildren(folderId);

      const folderIdsWithChildren = collectFolderIdsWithChildren(result);
      setLoading(false);
      setOpenIds((prevOpenIds) => {
        const newOpenIds = { ...prevOpenIds };
        [folderId, ...folderIdsWithChildren].forEach((id) => {
          newOpenIds[id] = true;
        });
        return newOpenIds;
      });
    } catch (error) {
      log(error);
      setLoading(false);
    }
  }, [folderId, setOpenIds, queryClient, setLoading]);

  const tMenu = useTranslations('menu.tree');

  return (
    <MenuItem icon={FolderDownIcon} onSelect={handleSelect} disabled={disabled} dataCy="expand-children-folders-action">
      {tMenu('expandAllChildren')}
    </MenuItem>
  );
};

type CollapseChildrenFoldersMenuItemProps = BaseMenuVisibilityProps & {
  folderId: string;
  tree: Array<TreeRecursiveChildrenNode>;
  setOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
};

export const CollapseChildrenFoldersMenuItem = ({
  folderId,
  tree,
  setOpenIds,
  disabled,
}: CollapseChildrenFoldersMenuItemProps) => {
  const findFolderNode = (
    nodes: Array<TreeRecursiveChildrenNode>,
    targetId: string,
  ): TreeRecursiveChildrenNode | null => {
    for (const node of nodes) {
      if (node.id === targetId && node.type === DAMNodeType.Folder) {
        return node;
      }

      if (node.children?.length) {
        const found: TreeRecursiveChildrenNode | null = findFolderNode(node.children, targetId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const collectFolderIds = (nodes: Array<TreeRecursiveChildrenNode>): Array<string> => {
    const ids: Array<string> = [];

    for (const node of nodes) {
      if (node.type === DAMNodeType.Folder) {
        ids.push(node.id);

        if (node.children?.length) {
          ids.push(...collectFolderIds(node.children));
        }
      }
    }

    return ids;
  };

  const handleSelect = useCallback(() => {
    try {
      const parentFolder = findFolderNode(tree, folderId);
      if (!parentFolder) {
        return;
      }

      const childFolderIds = collectFolderIds(parentFolder.children ?? []);

      setOpenIds((prevOpen) => {
        const newOpenIds = { ...prevOpen };
        delete newOpenIds[folderId];
        childFolderIds.forEach((id) => delete newOpenIds[id]);
        return newOpenIds;
      });
    } catch (error) {
      log(error);
    }
  }, [folderId, tree, setOpenIds]);

  const tMenu = useTranslations('menu.tree');

  return (
    <MenuItem icon={FolderUpIcon} onSelect={handleSelect} disabled={disabled} dataCy="collapse-children-folders-action">
      {tMenu('collapseAllChildren')}
    </MenuItem>
  );
};

export const EmptyTrashMenuItem = ({ disabled }: BaseMenuVisibilityProps) => {
  const openModal = useOpenModal();
  const tMenu = useTranslations('menu.trash');
  const tDialogs = useTranslations('dialogs');
  const { isAuthenticated } = useAuth0();
  const { isTrashPage } = useCurrentPage();
  const { offset, limit, sortOrder, sortValue } = useDataTablePagination(EntityType.Trash);

  const { emptyTrash } = useTrash(
    {
      pagination: {
        offset,
        limit,
      },
      sort: {
        order: sortOrder,
        value: sortValue,
      },
    },
    { enabled: isAuthenticated && isTrashPage },
  );

  return (
    <MenuItem
      icon={Trash2}
      onSelect={() => {
        openModal(ModalId.DeleteConfirmationAlertDialog, {
          'content': tDialogs('emptyTrashDescription'),
          'onClick': () => {
            void emptyTrash();
          },
          'data-cy': 'empty-trash',
          'confirmLabel': tDialogs('emptyTrashAction'),
        });
      }}
      disabled={disabled}
      dataCy="empty-trash-action"
    >
      {tMenu('empty')}
    </MenuItem>
  );
};

export const AddAssetsToUploadMenuItem = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => {
  const tUpload = useTranslations('upload');

  return (
    <MenuItem icon={Upload} onSelect={() => onClick()} disabled={disabled} dataCy="add-assets-to-upload-action">
      {tUpload('addAssetsToUpload')}
    </MenuItem>
  );
};

export const ClearUploadMenuItem = ({
  disabled,
  keepExistingFoldersOnClear,
}: {
  disabled: boolean;
  /** Off for a contributor session, so clearing never re-enables folder recreation. */
  keepExistingFoldersOnClear?: boolean;
}) => {
  const tUpload = useTranslations('upload');
  const { getActiveUploadSession, clearAllFiles } = useUploadStore();
  const { uploadSessionId } = getActiveUploadSession();

  return (
    <MenuItem
      icon={ListX}
      onSelect={() => clearAllFiles(uploadSessionId, keepExistingFoldersOnClear)}
      disabled={disabled}
      dataCy="clear-upload-action"
    >
      {tUpload('clearUpload')}
    </MenuItem>
  );
};

export const RemoveFromFavorites = ({ node, disabled }: { node: Partial<TreeRootNode>; disabled?: boolean }) => {
  const { setFavorite } = useNodeFavorite();
  const tMenu = useTranslations('menu.favorites');

  return (
    <MenuItem
      icon={Trash2}
      onSelect={() => node.id && node.type && setFavorite(node.id, true, false, node.type as NodeType | DAMNodeType)}
      disabled={disabled}
      dataCy="remove-from-favorites-action"
    >
      {tMenu('remove')}
    </MenuItem>
  );
};

export const ActivateVersionMenuItem = ({
  asset,
  versionId,
  disabled,
}: {
  asset: Asset;
  versionId: string;
  disabled?: boolean;
}) => {
  const tCommon = useTranslations('common');
  const { activateAssetVersionMutation } = useActivateAssetVersion();

  return (
    <MenuItem
      icon={MousePointerClick}
      iconClassName="text-green-500!"
      onSelect={() => activateAssetVersionMutation.mutate({ asset_id: asset.id, version_id: versionId })}
      disabled={disabled}
      dataCy="activate-version-action"
    >
      {tCommon('activate')}
    </MenuItem>
  );
};

export const ViewVersionMenuItem = ({
  assetVersion,
  disabled,
}: {
  assetVersion: NonNullable<Asset['versions']>[number];
  disabled?: boolean;
}) => {
  const tCommon = useTranslations('common');
  const openModal = useOpenModal();

  return (
    <MenuItem
      icon={Eye}
      onSelect={() => openModal(ModalId.VersionPreview, { assetVersion: assetVersion })}
      dataCy="view-version-action"
      disabled={disabled}
    >
      {tCommon('view')}
    </MenuItem>
  );
};

export const DeleteVersionMenuItem = ({
  assetId,
  versionId,
  disabled,
  danger,
}: {
  assetId: string;
  versionId: string;
  disabled?: boolean;
  danger?: boolean;
}) => {
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const openModal = useOpenModal();

  const { toast } = useToast();

  const tToasts = useTranslations('toasts');
  const queryClient = useQueryClient();

  const deleteAssetVersionWithAuth = useAuthenticatedQueryFn(deleteAssetVersion);

  const deleteAssetVersionMutation = useMutation({
    mutationFn: deleteAssetVersionWithAuth,
    onSuccess: () => {
      toast({
        title: tToasts('deleteAssetVersionMutation.title'),
        description: tToasts('deleteAssetVersionMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset] });
    },
    onError: (err) => {
      log(err);
    },
  });

  return (
    <MenuItem
      icon={Trash2}
      onSelect={() => {
        openModal(ModalId.DeleteConfirmationAlertDialog, {
          'content': tDialogs('deleteAssetVersionDescription'),
          'onClick': () =>
            assetId &&
            versionId &&
            deleteAssetVersionMutation.mutate({
              asset_id: assetId,
              version_id: versionId,
            }),
          'data-cy': 'delete-version',
        });
      }}
      dataCy="delete-version-action"
      disabled={disabled}
      danger={danger}
    >
      {tCommon('delete')}
    </MenuItem>
  );
};

export const RestoreTrashMenuItem = ({ label, disabled }: { label: string; disabled?: boolean }) => {
  const openModal = useOpenModal();

  return (
    <MenuItem
      icon={RotateCcwIcon}
      onSelect={() => openModal(ModalId.RestoreTrashConfirmation)}
      dataCy="restore-trash-action"
      disabled={disabled}
    >
      {label}
    </MenuItem>
  );
};

export const DownloadMenuItem = <TAsset extends Asset>({
  asset,
  disabled,
}: {
  asset: Row<TAsset>;
  disabled?: boolean;
}) => {
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const download = useDownload({
    title: tToasts('asset.downloading.title'),
    description: tToasts('asset.downloading.description'),
  });

  return (
    <MenuItem
      icon={Download}
      onSelect={() => download(asset.original.url!, asset.original.fileName!)}
      disabled={disabled}
      dataCy="download-action"
    >
      {tCommon('download')}
    </MenuItem>
  );
};

export const DuplicateAssetMenuItem = <TAsset extends Asset>({
  asset,
  selectedAssets,
  destinationFolderId,
  isAllSelected,
  rowCount,
  includeSubFolderAssets,
  disabled,
}: {
  asset: Row<TAsset>;
  /** Deliberately Asset, not TAsset, independent of whichever row type this table uses. */
  selectedAssets: Array<Asset>;
  destinationFolderId: string;
  isAllSelected: boolean;
  rowCount: number;
  includeSubFolderAssets: boolean;
  disabled?: boolean;
}) => {
  const tCommon = useTranslations('common');
  const { duplicateAsset, confirmDuplicateAssets, confirmDuplicateAllAssets } = useDuplicateAssetAction();

  return (
    <MenuItem
      icon={Copy}
      onSelect={() => {
        if (isAllSelected) {
          confirmDuplicateAllAssets(rowCount, destinationFolderId, includeSubFolderAssets);
          return;
        }
        if (selectedAssets.length > 1) {
          confirmDuplicateAssets(selectedAssets, destinationFolderId);
          return;
        }
        if (asset.original.id && asset.original.name) {
          void duplicateAsset(asset.original.id, destinationFolderId, asset.original.name);
        }
      }}
      disabled={disabled}
      dataCy="duplicate-action"
    >
      {tCommon('duplicate')}
    </MenuItem>
  );
};

export const OpenParentFolderMenuItem = <TAsset extends Asset>({
  asset,
  disabled,
}: {
  asset: Row<TAsset>;
  disabled?: boolean;
}) => {
  const { setLocateAssetId } = useBrowserStore();
  const { browseRedirection } = useCurrentPage();
  const tMenu = useTranslations('menu.folder');

  const getAssetFn = useAuthenticatedQueryFn(getAsset);
  return (
    <MenuItem
      icon={FolderOpen}
      onSelect={async () => {
        if (asset.original.id && asset.original.parentFolderId) {
          const assetInfo = await getAssetFn({ id: asset.original.id });
          setLocateAssetId(asset.original.id);
          if (assetInfo.ancestors && assetInfo.ancestors.length > 0) {
            const pathArray = assetInfo.ancestors.map((ancestor) => ancestor.slug);
            // Drop both query and filters so revealing an asset fully exits search mode.
            // search is now derived from the URL — a lingering `filters` param would re-activate it
            // once locateAssetId clears.
            await browseRedirection(pathArray, undefined, undefined, ['query', 'filters']);
          }
        }
      }}
      disabled={disabled}
      dataCy={`open-parent-folder-${asset.original.id}`}
    >
      {tMenu('openParent')}
    </MenuItem>
  );
};

export const ToggleViewMenu = ({
  showFoldersAndAlbumsToggle = false,
}: {
  /**
   * The setting only has an effect on browse (gallery-layout.tsx gates `childNodes` on it), but
   * this menu is shared by trash, shared-with-me, duplicates, ingestion and the public share
   * gallery too — so the checkbox stays hidden unless the caller is the one surface it acts on.
   */
  showFoldersAndAlbumsToggle?: boolean;
}) => {
  const primitives = useMenuPrimitives();
  const tCommon = useTranslations('common');

  const { RadioGroup, RadioItem } = primitives;
  const galleryView = useUserSettingsStore((s) => s.galleryView);
  const setGalleryView = useUserSettingsStore((s) => s.setGalleryView);
  const showFoldersAndAlbums = useUserSettingsStore((s) => s.showFoldersAndAlbums);
  const setShowFoldersAndAlbums = useUserSettingsStore((s) => s.setShowFoldersAndAlbums);

  const options = [
    { label: 'Tile', value: GalleryView.TILE },
    { label: 'List', value: GalleryView.LIST },
  ];

  return (
    <>
      <RadioGroup value={galleryView} onValueChange={(value: string) => setGalleryView(value as GalleryView)}>
        {options.map((option) => (
          <RadioItem key={option.value} value={option.value}>
            {option.label}
          </RadioItem>
        ))}
      </RadioGroup>
      <MenuCheckboxItem
        hidden={!showFoldersAndAlbumsToggle}
        checked={showFoldersAndAlbums}
        onCheckedChange={setShowFoldersAndAlbums}
      >
        {tCommon('showFoldersAndAlbums')}
      </MenuCheckboxItem>
    </>
  );
};

export const SortOrderMenu = ({ tableEntity }: { tableEntity: EntityType }) => {
  const primitives = useMenuPrimitives();
  const setPagination = useTableStore((s) => s.setPagination);
  const getTableSettings = useTableStore((s) => s.getTableSettings);

  const { RadioGroup, RadioItem } = primitives;
  const { sortOrder } = getTableSettings(tableEntity);
  const { setLocateAssetId } = useBrowserStore();

  const options = baseSortOrderOptions.map((option) => ({ label: option.label, value: option.value }));

  return (
    <RadioGroup
      value={sortOrder as string}
      onValueChange={(order: string) => {
        setLocateAssetId(undefined);
        setPagination(tableEntity, { sortOrder: order as SortOrderOptions });
      }}
    >
      {options.map((option) => (
        <RadioItem key={option.value} value={option.value}>
          {option.label}
        </RadioItem>
      ))}
    </RadioGroup>
  );
};

type SearchSortByApi = {
  currentRefinement: string;
  options: Array<{ label: string; value: string }>;
  refine: (value: string) => void;
};

export const SortByValueMenu = ({
  tableEntity,
  searchSortBy,
}: {
  tableEntity: EntityType;
  searchSortBy?: SearchSortByApi;
}) => {
  const primitives = useMenuPrimitives();
  const setPagination = useTableStore((s) => s.setPagination);
  const getTableSettings = useTableStore((s) => s.getTableSettings);
  const selectedNode = useTreeStore((state) => state.selectedNode);

  const { RadioGroup, RadioItem } = primitives;
  const { setLocateAssetId } = useBrowserStore();
  const { isTrashPage, isSharesPage } = useCurrentPage();
  const { sortValue } = getTableSettings(tableEntity);
  const search = useIsSearching();

  const searchOptions = searchSortBy?.options ?? [];

  const options = useMemo(() => {
    if (isTrashPage) {
      return trashSortOptions as ReadonlyArray<{ label: string; value: string }>;
    }

    if (isSharesPage) {
      return shareSortOptions as ReadonlyArray<{ label: string; value: string }>;
    }

    if (search) {
      return searchOptions as ReadonlyArray<{ label: string; value: string }>;
    }

    if (selectedNode?.type === DAMNodeType.Album) {
      return albumSortOptions as ReadonlyArray<{ label: string; value: string }>;
    }

    return (sortOptionsByEntity[tableEntity] ?? baseSortOptions) as ReadonlyArray<{ label: string; value: string }>;
  }, [isTrashPage, isSharesPage, search, selectedNode?.type, searchOptions, tableEntity]);

  return (
    <RadioGroup
      value={search ? (searchSortBy?.currentRefinement ?? '') : sortValue}
      onValueChange={(value: string) => {
        if (search) {
          searchSortBy?.refine(value);
        } else {
          setLocateAssetId(undefined);
          setPagination(tableEntity, { sortValue: value as SortOrderValues });
        }
      }}
    >
      {options.map((option) => (
        <RadioItem key={option.value} value={option.value}>
          {option.label}
        </RadioItem>
      ))}
    </RadioGroup>
  );
};

export const PublicSortByValueMenu = ({ tableEntity }: { tableEntity: EntityType }) => {
  const primitives = useMenuPrimitives();
  const setPagination = useTableStore((s) => s.setPagination);
  const getTableSettings = useTableStore((s) => s.getTableSettings);

  const { RadioGroup, RadioItem } = primitives;
  const { setLocateAssetId } = useBrowserStore();
  const { sortValue } = getTableSettings(tableEntity);

  const options = albumSortOptions as ReadonlyArray<{ label: string; value: string }>;

  return (
    <RadioGroup
      value={sortValue as string}
      onValueChange={(value: string) => {
        setLocateAssetId(undefined);
        setPagination(tableEntity, { sortValue: value as SortOrderValues });
      }}
    >
      {options.map((option) => (
        <RadioItem key={option.value} value={option.value}>
          {option.label}
        </RadioItem>
      ))}
    </RadioGroup>
  );
};

export const ColumnsVisibilitySubmenu = ({ table }: { table: any }) => {
  const primitives = useMenuPrimitives();
  const visibility = table.getState().columnVisibility;
  const tCommon = useTranslations('common');

  const { Sub, SubTrigger, SubContent, CheckboxItem } = primitives;
  const galleryView = useUserSettingsStore((s) => s.galleryView);
  const isListView = galleryView === GalleryView.LIST;
  const Icon = isListView ? Columns3 : Rows3;

  const columns = table
    .getAllColumns()
    .filter((column: { getCanHide: () => any }) => column.getCanHide())
    .filter((column: { id: ColumnType }) => {
      if ([ColumnType.Name, ColumnType.Thumbnail].includes(column.id)) {
        return false;
      }

      if (
        galleryView === GalleryView.TILE &&
        [
          ColumnType.DragHandle,
          ColumnType.ParentFolderName,
          ColumnType.Dimensions,
          ColumnType.Select,
          ColumnType.Count,
        ].includes(column.id)
      ) {
        return false;
      }

      return true;
    })
    .map((column: { id: ColumnType; getIsVisible: () => any; toggleVisibility: (arg0: boolean) => any }) => ({
      id: String(column.id),
      label:
        column.id === ColumnType.NodeId
          ? 'Asset Id'
          : column.id === ColumnType.FileName
            ? 'File Name'
            : column.id === ColumnType.ParentFolderName
              ? 'Parent'
              : column.id === ColumnType.CommentCount
                ? 'Comments'
                : String(column.id),
      checked: visibility?.[String(column.id)] ?? column.getIsVisible(),
      onCheckedChange: (checked: boolean) => column.toggleVisibility(checked),
    }));

  const hasColumns = columns.length > 0;

  return (
    <Sub>
      <SubTrigger disabled={!hasColumns} className="flex items-center">
        <Icon className="mr-2 icon-6 text-icon-base" />
        {isListView ? tCommon('columns') : tCommon('fields')}
      </SubTrigger>
      <SubContent>
        {columns.map(
          (column: {
            id: Key | null | undefined;
            checked: any;
            onCheckedChange: (arg0: boolean) => any;
            label: any;
          }) => (
            <CheckboxItem
              key={column.id}
              checked={column.checked}
              onCheckedChange={(checked: boolean) => column.onCheckedChange(checked === true)}
              className="capitalize"
            >
              {column.label}
            </CheckboxItem>
          ),
        )}
      </SubContent>
    </Sub>
  );
};

export const ZoomInMenuItem = () => {
  const isZoomInDisabled = useUserSettingsStore(selectIsZoomInDisabled);
  const zoomIn = useUserSettingsStore((s) => s.zoomIn);
  const tMenu = useTranslations('menu.zoom');

  return (
    <MenuItem icon={ZoomIn} onSelect={zoomIn} disabled={isZoomInDisabled}>
      {tMenu('in')}
    </MenuItem>
  );
};

export const ZoomOutMenuItem = () => {
  const isZoomOutDisabled = useUserSettingsStore(selectIsZoomOutDisabled);
  const zoomOut = useUserSettingsStore((s) => s.zoomOut);
  const tMenu = useTranslations('menu.zoom');

  return (
    <MenuItem icon={ZoomOut} onSelect={zoomOut} disabled={isZoomOutDisabled}>
      {tMenu('out')}
    </MenuItem>
  );
};

export enum ThemeOption {
  Light = 'light',
  Dark = 'dark',
  System = 'system',
}

export const ThemeMenuSubmenu = () => {
  const tTheme = useTranslations('menu.theme');
  const primitives = useMenuPrimitives();
  const { Sub, SubTrigger, SubContent } = primitives;
  const { setTheme, theme } = useTheme();

  const normalizedTheme = theme ?? ThemeOption.System;
  const TriggerIcon =
    normalizedTheme === ThemeOption.Light ? Sun : normalizedTheme === ThemeOption.Dark ? Moon : Contrast;

  return (
    <Sub>
      <SubTrigger className="flex items-center">
        <TriggerIcon className="mr-2 icon-6 text-icon-base" />
        {tTheme('title')}
      </SubTrigger>
      <SubContent>
        <MenuItem
          icon={Sun}
          disabled={normalizedTheme === ThemeOption.Light}
          onSelect={() => setTheme(ThemeOption.Light)}
        >
          {tTheme('light')}
        </MenuItem>
        <MenuItem
          icon={Moon}
          disabled={normalizedTheme === ThemeOption.Dark}
          onSelect={() => setTheme(ThemeOption.Dark)}
        >
          {tTheme('dark')}
        </MenuItem>
        <MenuItem
          icon={Contrast}
          disabled={normalizedTheme === ThemeOption.System}
          onSelect={() => setTheme(ThemeOption.System)}
        >
          {tTheme('system')}
        </MenuItem>
      </SubContent>
    </Sub>
  );
};

export const LogoutMenuItem = () => {
  const { performLogout } = usePerformLogout();
  const tMenu = useTranslations('menu.user');

  return (
    <MenuItem icon={LogOut} onSelect={performLogout} dataCy="log-out-action">
      {tMenu('logOut')}
    </MenuItem>
  );
};

export const EditCommentMenuItem = ({ onSelect }: { onSelect: () => void }) => {
  const tCommon = useTranslations('common');

  return (
    <MenuItem icon={Pen} onSelect={onSelect} dataCy="edit-comment-action">
      {tCommon('edit')}
    </MenuItem>
  );
};

export const DeleteCommentMenuItem = ({
  comment,
  isReply,
  hasReplies,
  parentCommentId,
  onDeleteStart,
  onSuccess,
  onConfirmationOpen,
  disabled = false,
}: {
  comment: Pick<NodeComment, 'nodeId' | 'commentId'>;
  hasReplies?: boolean;
  isReply?: boolean;
  parentCommentId?: string;
  onDeleteStart?: () => void;
  onSuccess?: (deletedCommentId: string) => void;
  /** Receives the confirmation's modal key so the caller can keep its own UI pinned while it is open. */
  onConfirmationOpen?: (modalKey: string) => void;
  disabled?: boolean;
}) => {
  const openModal = useOpenModal();

  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');

  const { deleteNodeCommentMutation } = useDeleteNodeCommentMutation();

  const dialogContent = isReply
    ? 'deleteReplyConfirmation'
    : hasReplies
      ? 'deleteCommentAndRepliesConfirmation'
      : 'deleteCommentConfirmation';

  return (
    <MenuItem
      icon={Trash2}
      disabled={disabled}
      onSelect={() => {
        const modalKey = openModal(ModalId.DeleteConfirmationAlertDialog, {
          'content': tDialogs(dialogContent),
          'onClick': () => {
            if (comment) {
              onDeleteStart?.();
              deleteNodeCommentMutation.mutate({
                id: comment.commentId,
                nodeId: comment.nodeId,
                parentCommentId,
                onSuccessCallback: onSuccess,
              });
            }
          },
          'data-cy': 'delete-comment',
        });

        onConfirmationOpen?.(modalKey);
      }}
      dataCy="delete-comment-action"
      danger
    >
      {tCommon('delete')}
    </MenuItem>
  );
};

export const SortCommentsMenu = ({
  value = SortOrderOptions.DESC,
  onValueChange,
  sortValue = SortOrderValues.CREATED_AT,
  onSortValueChange,
}: {
  value?: SortOrderOptions;
  onValueChange?: (value: SortOrderOptions) => void;
  sortValue?: SortOrderValues;
  onSortValueChange?: (value: SortOrderValues) => void;
}) => {
  const primitives = useMenuPrimitives();

  const { RadioGroup, RadioItem, Separator } = primitives;

  return (
    <>
      <RadioGroup
        value={sortValue}
        onValueChange={(nextValue: string) => onSortValueChange?.(nextValue as SortOrderValues)}
      >
        {baseSortOptions.map((option) => (
          <RadioItem key={option.value} value={option.value}>
            {option.label}
          </RadioItem>
        ))}
      </RadioGroup>
      <Separator />
      <RadioGroup value={value} onValueChange={(nextValue: string) => onValueChange?.(nextValue as SortOrderOptions)}>
        {baseSortOrderOptions.map((option) => (
          <RadioItem key={option.value} value={option.value}>
            {option.label}
          </RadioItem>
        ))}
      </RadioGroup>
    </>
  );
};

export const FilterCommentsMenu = ({
  value,
  onValueChange,
  visibilityValue,
  onVisibilityValueChange,
  canFilterByVisibility = true,
}: {
  value?: CommentResolutionFilterOption;
  onValueChange?: (value: CommentResolutionFilterOption | undefined) => void;
  visibilityValue?: CommentVisibilityFilterOption;
  onVisibilityValueChange?: (value: CommentVisibilityFilterOption | undefined) => void;
  canFilterByVisibility?: boolean;
}) => {
  const tComments = useTranslations('inspector.comments');
  const primitives = useMenuPrimitives();

  const { RadioGroup, RadioItem, Separator } = primitives;

  // "All" is mutually exclusive with resolution/visibility: selecting it clears both of the
  // others. Resolution and visibility are otherwise independent and can be combined freely, so
  // "All" is only shown as selected when neither of the other two axes is active.
  const isAllSelected = !value && !visibilityValue;

  return (
    <>
      <RadioGroup
        value={isAllSelected ? CommentAllFilter.ALL : ''}
        onValueChange={(nextValue: string) => {
          if (nextValue === CommentAllFilter.ALL) {
            onValueChange?.(undefined);
            onVisibilityValueChange?.(undefined);
          }
        }}
      >
        <RadioItem data-cy="radio-item-comment-filter-all" value={CommentAllFilter.ALL}>
          {tComments('filterAll')}
        </RadioItem>
      </RadioGroup>
      <Separator />
      <RadioGroup
        value={value ?? ''}
        onValueChange={(nextValue: string) => onValueChange?.(nextValue as CommentResolutionFilterOption)}
      >
        <RadioItem data-cy="radio-item-comment-filter-resolved" value={CommentResolutionFilter.RESOLVED}>
          {tComments('resolutionFilterResolved')}
        </RadioItem>
        <RadioItem data-cy="radio-item-comment-filter-unresolved" value={CommentResolutionFilter.UNRESOLVED}>
          {tComments('resolutionFilterUnresolved')}
        </RadioItem>
      </RadioGroup>
      {canFilterByVisibility && (
        <>
          <Separator />
          <RadioGroup
            value={visibilityValue ?? ''}
            onValueChange={(nextValue: string) => onVisibilityValueChange?.(nextValue as CommentVisibilityFilterOption)}
          >
            <RadioItem data-cy="radio-item-comment-filter-public" value={CommentVisibilityFilter.PUBLIC}>
              {tComments('visibilityFilterPublic')}
            </RadioItem>
            <RadioItem data-cy="radio-item-comment-filter-private" value={CommentVisibilityFilter.PRIVATE}>
              {tComments('visibilityFilterPrivate')}
            </RadioItem>
          </RadioGroup>
        </>
      )}
    </>
  );
};

export const DownloadZipMenuItem = ({
  nodeIds,
  name,
  sourceType,
  assets,
  disabled,
}: {
  nodeIds: Array<string>;
  name?: string;
  sourceType: DAMNodeType | NodeType.Assets;
  assets?: Array<Asset>;
  disabled?: boolean;
}) => {
  const openModal = useOpenModal();
  const tMenu = useTranslations('menu.node');

  const exportNodeIds = nodeIds ?? [];

  if (exportNodeIds.length === 0) {
    return null;
  }

  return (
    <MenuItem
      icon={FileArchive}
      onSelect={() => openModal(ModalId.DownloadZip, { nodeIds: exportNodeIds, sourceType, name, assets })}
      disabled={disabled}
      dataCy="download-zip-action"
    >
      {tMenu('downloadZip')}
    </MenuItem>
  );
};

/**
 * Share-scoped counterpart to DownloadZipMenuItem for guest/authenticated
 * viewers on a public share (anonymous viewers never see it — gated on
 * canDownloadAssets at the call site). Opens the share download dialog
 * instead of the authenticated one, and is gated on the share's downloadable
 * variants.
 */
export const ShareDownloadMenuItem = ({
  shareId,
  nodeIds,
  name,
  assets,
  downloadableVariants,
  includeOriginalFile,
}: {
  shareId: string;
  nodeIds: Array<string>;
  name?: string;
  assets?: Array<Asset>;
  downloadableVariants: Array<{ id: string; name: string; slug: string }>;
  includeOriginalFile?: boolean;
}) => {
  const openModal = useOpenModal();
  const tMenu = useTranslations('menu.node');

  if (nodeIds.length === 0) {
    return null;
  }

  return (
    <MenuItem
      icon={FileArchive}
      onSelect={() =>
        openModal(ModalId.ShareDownload, { shareId, nodeIds, downloadableVariants, name, assets, includeOriginalFile })
      }
      disabled={!shareHasDownloadableContent({ downloadableVariants, includeOriginalFile })}
      dataCy="share-download-action"
    >
      {tMenu('downloadZip')}
    </MenuItem>
  );
};
